import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import type { PublicCatalogDevice } from '@/features/operations/api';

/**
 * One catalogue device. `grid` is the tall card of the desktop grid; `list` puts the photo beside
 * the data, so a phone shows several devices per screen, as Amazon and Mercado Libre do.
 */
export function CatalogDeviceCard({
  device,
  price,
  variant,
}: {
  device: PublicCatalogDevice;
  price: string;
  variant: 'grid' | 'list';
}) {
  const list = variant === 'list';
  const name = `${device.brand} ${device.model}`;
  const details = [
    device.storageGb === null ? null : `${device.storageGb} GB`,
    device.color,
    device.batteryHealth === null ? null : `Batería ${device.batteryHealth}%`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        component={Link}
        to={`/catalog/${device.id}`}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: list ? 'row' : 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: list ? 112 : '100%',
            height: list ? 112 : 220,
            m: list ? 1.5 : 0,
            borderRadius: list ? '8px' : 0,
            overflow: 'hidden',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.disabled',
          }}
        >
          {device.photos[0] ? (
            <Box
              component="img"
              src={device.photos[0]}
              alt={`${name}${device.color ? ` color ${device.color}` : ''}`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <PhotoCameraOutlinedIcon aria-label="Sin foto" />
          )}
        </Box>
        <Stack spacing={list ? 0.5 : 1} sx={{ p: list ? 1.5 : 2, pl: list ? 0 : 2, minWidth: 0 }}>
          <Typography variant={list ? 'subtitle1' : 'h6'} component="h3" sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {details}
          </Typography>
          <Typography variant={list ? 'subtitle1' : 'h6'} component="p" sx={{ fontWeight: 700 }}>
            {price}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
